import { Order } from '~api/tos';

import { Fragment, useRef } from 'react';

import { EmptyState, InteractiveTable, LoadingTableRows, PileBadge, Table } from '@ingrid/luterkort-react';

import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { TableAddressSecond } from '~src/components/TableAddress/TableAddress';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';

import { DetailsDrawer } from './details-drawer';
import { Menu } from './menu';
import { getSourceLabel } from './source';

interface Props {
  isEmpty: boolean;
  isLoading: boolean;
  loadRowCount: number;
  transportOrders: Order[];
}

export function ListTable(props: Props) {
  const { isEmpty, isLoading, loadRowCount, transportOrders } = props;
  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const tableRef = useRef<HTMLTableElement>(null);
  const order = transportOrders.find(o => o.id === highlightedKey);

  if (isEmpty) {
    return <EmptyState title={t('transportOrders.list.notFound')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={tableRef}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '40%' }}>{t('common.orderId')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('common.createdAt')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('common.source')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('common.shipFrom')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('common.shipTo')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {transportOrders.map(order => (
            <InteractiveTable.Row key={order.id} value={order.id!}>
              <Table.Cell>
                <Ellipsis>{order.id}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <LocalizedDate dateTime={order.created_at!} short time />
              </Table.Cell>
              <Table.Cell>
                <PileBadge css={{ display: 'inline-flex', maxWidth: '100%' }} variant="neutral">
                  <Ellipsis>{t(getSourceLabel(order.metadata?.source))}</Ellipsis>
                </PileBadge>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{order.sender?.contact_info?.address?.name ?? '-'}</Ellipsis>
                <TableAddressSecond>{order.sender?.contact_info?.address?.city}</TableAddressSecond>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{order.receiver?.contact_info?.address?.name ?? '-'}</Ellipsis>
                <TableAddressSecond>{order.receiver?.contact_info?.address?.city}</TableAddressSecond>
              </Table.Cell>
              <Table.Cell>
                <Menu listing order={order} />
              </Table.Cell>
            </InteractiveTable.Row>
          ))}
          {isLoading && <LoadingTableRows tableRef={tableRef} rowCount={loadRowCount} />}
        </tbody>
      </InteractiveTable.Table>

      <DetailsDrawer order={order} />
    </Fragment>
  );
}
